import React from "react";
import { useState, useEffect } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Box,
  Grid,
  Typography,
  CircularProgress,
  InputAdornment,
  Stack,
  Snackbar,
  Alert,
  Paper,
  Divider,
  Avatar,
  Tooltip,
} from "@mui/material";

// Icons
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import NumbersIcon from "@mui/icons-material/Numbers";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import KeyIcon from "@mui/icons-material/Key";

import { API_URL } from "../../Config/api";

const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const PHONE_REGEX = /^[6-9]\d{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PINCODE_REGEX = /^[1-9][0-9]{5}$/;

const BankDetailsForm = ({
  srv_prof_id,
  prof_mail_id,
  phone_no,
  prof_fullname,
  onUpdateSuccess,
  onSuccess,
}) => {
  const [form, setForm] = useState({
    bank_account: "",
    confirm_bank_account: "",
    ifsc: "",
    payout_type: 1,
    prof_id: srv_prof_id,
    beneficiary_name: prof_fullname || "",
    beneficiary_email: "",
    beneficiary_phone: "",
    beneficiary_country_code: "+91",
    beneficiary_address: "",
    beneficiary_city: "",
    beneficiary_state: "",
    beneficiary_postal_code: "",
  });

  const [errors, setErrors] = useState({
    phone: "",
    email: "",
    pincode: "",
    ifsc: "",
  });

  const [existingBeneficiaryId, setExistingBeneficiaryId] = useState(null);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [checkingAccount, setCheckingAccount] = useState(false);
  const [accountMatch, setAccountMatch] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null); // null, 'VERIFIED', or other statuses

  const showToast = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleFinalizeWithDelay = () => {
    setTimeout(() => {
      if (onSuccess) onSuccess();
      if (onUpdateSuccess) onUpdateSuccess();
    }, 1500);
  };

  /* ================== FETCH & AUTO-FILL LOGIC ================== */
  useEffect(() => {
    const loadInitialData = async () => {
      if (!srv_prof_id) return;
      setPageLoading(true);

      try {
        const res = await axios.get(
          `${API_URL}/pay_out/cashfree_beneficiary/?prof_id=${srv_prof_id}`,
        );

        if (res.data && res.data.length > 0) {
          const d = res.data[0];
          setForm((prev) => ({
            ...prev,
            beneficiary_name: d.beneficiary_name || prof_fullname || "",
            bank_account: d.bank_account_number || "",
            confirm_bank_account: d.bank_account_number || "",
            ifsc: d.bank_ifsc || "",
            beneficiary_email: d.beneficiary_email || "",
            beneficiary_address: d.beneficiary_address || "",
            beneficiary_city: d.beneficiary_city || "",
            beneficiary_state: d.beneficiary_state || "",
            beneficiary_postal_code: d.beneficiary_postal_code || "",
            beneficiary_phone: d.beneficiary_phone
              ? d.beneficiary_phone.replace("+91", "").slice(-10)
              : "",
          }));
          setExistingBeneficiaryId(d.beneficiary_id);
          setIsDataFetched(true);
          setAccountMatch(true);
        } else {
          setForm((prev) => ({
            ...prev,
            beneficiary_name: prof_fullname || prev.beneficiary_name,
            beneficiary_email: prof_mail_id || "",
            beneficiary_phone: phone_no
              ? phone_no.toString().replace("+91", "").slice(-10)
              : "",
            prof_id: srv_prof_id,
          }));
          setIsDataFetched(false);
          setAccountMatch(null);
        }
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setPageLoading(false);
      }
    };

    loadInitialData();
  }, [srv_prof_id, prof_mail_id, phone_no, prof_fullname]);

  /* ================== HANDLERS (Fixed Logic) ================== */
  const handleChange = (key, value) => {
    let currentError = "";

    // ✅ Case 1: Phone (Numbers Only)
    if (key === "beneficiary_phone") {
      const onlyNums = value.replace(/[^0-9]/g, "");
      setForm((prev) => ({ ...prev, [key]: onlyNums }));
      currentError =
        onlyNums && !PHONE_REGEX.test(onlyNums)
          ? "Invalid 10-digit number"
          : "";
      setErrors((prev) => ({ ...prev, phone: currentError }));
    }
    // ✅ Case 2: Bank Accounts (Numbers Only) - Yahan logic move kiya hai
    else if (key === "bank_account" || key === "confirm_bank_account") {
      const onlyNums = value.replace(/[^0-9]/g, "");
      setForm((prev) => ({ ...prev, [key]: onlyNums }));
    }
    // ✅ Case 3: Email
    else if (key === "beneficiary_email") {
      setForm((prev) => ({ ...prev, [key]: value }));
      currentError =
        value && !EMAIL_REGEX.test(value) ? "Invalid email address" : "";
      setErrors((prev) => ({ ...prev, email: currentError }));
    }
    // ✅ Case 4: IFSC (Auto Correct 'O' -> '0')
    else if (key === "ifsc") {
      let upper = value.toUpperCase();
      if (upper.length >= 5 && upper[4] === "O") {
        upper = upper.substring(0, 4) + "0" + upper.substring(5);
      }
      setForm((prev) => ({ ...prev, ifsc: upper }));
      const isValid = IFSC_REGEX.test(upper);
      setErrors((prev) => ({
        ...prev,
        ifsc: isValid ? "" : "Invalid IFSC (Example: HDFC0001234)",
      }));
    }
    // ✅ Default Case
    else {
      setForm((prev) => ({ ...prev, [key]: value }));
    }
  };

  const handleSave = async () => {
    // 1. Direct Check
    const isAccountMatching =
      form.bank_account === form.confirm_bank_account &&
      form.bank_account !== "";

    const isPhoneValid = PHONE_REGEX.test(form.beneficiary_phone);
    const isEmailValid = EMAIL_REGEX.test(form.beneficiary_email);
    const isIFSCValid = IFSC_REGEX.test(form.ifsc);

    // 2. Debugging
    console.log("=== Validation Debug ===");
    console.log("Phone Valid:", isPhoneValid);
    console.log("Email Valid:", isEmailValid);
    console.log("IFSC Valid:", isIFSCValid);
    console.log("Account Match:", isAccountMatching);
    console.log("========================");

    // 3. Validation Messages
    if (!isPhoneValid) {
      showToast("Invalid Phone Number (Must be 10 digits)", "error");
      return;
    }
    if (!isEmailValid) {
      showToast("Invalid Email Address", "error");
      return;
    }
    if (!isIFSCValid) {
      showToast("Invalid IFSC Code (5th character must be Zero '0')", "error");
      return;
    }
    if (!isAccountMatching) {
      showToast("Account numbers do not match or are empty", "error");
      return;
    }

    setLoading(true);
    const { confirm_bank_account, ...payload } = form;

    try {
      await axios.post(`${API_URL}/pay_out/add-beneficiary/`, payload);

      showToast("Bank details saved successfully!", "success");
      handleFinalizeWithDelay();
    } catch (err) {
      console.error("API Error:", err);
      showToast(
        err.response?.data?.message || "Failed to save details",
        "error",
      );
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!existingBeneficiaryId) return;
    setLoading(true);
    try {
      await axios.delete(
        `${API_URL}/pay_out/cashfree-beneficiary-delete/?beneficiary_id=${existingBeneficiaryId}`,
      );
      showToast("Beneficiary deleted successfully!", "info");
      handleFinalizeWithDelay();
    } catch (err) {
      showToast("Error deleting beneficiary", "error");
      setLoading(false);
    }
  };

  // Account Matching Logic
  useEffect(() => {
    if (!form.confirm_bank_account || isDataFetched) return;
    setCheckingAccount(true);
    const timer = setTimeout(() => {
      setAccountMatch(form.bank_account === form.confirm_bank_account);
      setCheckingAccount(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [form.bank_account, form.confirm_bank_account, isDataFetched]);

  if (pageLoading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <Paper
        elevation={3}
        sx={{
          maxWidth: 900,
          width: "100%",
          p: 4,
          borderRadius: 3,
          background: "linear-gradient(to bottom, #ffffff, #fcfcfc)",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} mb={3}>
          <Avatar sx={{ bgcolor: "#1976d2", width: 48, height: 48 }}>
            <AccountBalanceIcon fontSize="medium" />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight="bold" color="#333">
              Bank Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage payout beneficiary information
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        {/* Section 1: Beneficiary Details */}
        <Typography
          variant="subtitle2"
          fontWeight="bold"
          color="primary"
          sx={{ mb: 2, letterSpacing: 1, textTransform: "uppercase" }}
        >
          Beneficiary Information
        </Typography>

        <Grid container spacing={2} mb={4}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Account Holder Name"
              fullWidth
              size="small"
              value={form.beneficiary_name}
              disabled
              variant="filled"
              multiline
              maxRows={2}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0.5 }}>
                    <PersonIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Email Address"
              fullWidth
              size="small"
              value={form.beneficiary_email}
              onChange={(e) =>
                handleChange("beneficiary_email", e.target.value)
              }
              error={!!errors.email}
              helperText={errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Phone Number"
              fullWidth
              size="small"
              value={form.beneficiary_phone}
              onChange={(e) =>
                handleChange("beneficiary_phone", e.target.value)
              }
              error={!!errors.phone}
              helperText={errors.phone}
              inputProps={{ maxLength: 10 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "bold",
                        mr: 0.5,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <PhoneIcon
                        fontSize="small"
                        sx={{ mr: 0.5, color: "action.active" }}
                      />{" "}
                      +91
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        {/* Section 2: Banking Details */}
        <Typography
          variant="subtitle2"
          fontWeight="bold"
          color="primary"
          sx={{ mb: 2, letterSpacing: 1, textTransform: "uppercase" }}
        >
          Banking Information
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="IFSC Code"
              placeholder="e.g. HDFC0001234"
              fullWidth
              size="small"
              value={form.ifsc}
              onChange={(e) => handleChange("ifsc", e.target.value)}
              error={!!errors.ifsc}
              helperText={errors.ifsc}
              inputProps={{ maxLength: 11 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <KeyIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Account Number"
              fullWidth
              size="small"
              value={form.bank_account}
              // 🔥 FIX: Clean direct call
              onChange={(e) => handleChange("bank_account", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountBalanceWalletIcon color="action" />
                  </InputAdornment>
                ),
              }}
              inputProps={{ maxLength: 18 }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Confirm Account Number"
              fullWidth
              size="small"
              value={form.confirm_bank_account}
              // 🔥 FIX: Clean direct call
              onChange={(e) =>
                handleChange("confirm_bank_account", e.target.value)
              }
              error={accountMatch === false}
              helperText={
                accountMatch === false ? "Account numbers do not match" : ""
              }
              inputProps={{ maxLength: 18 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <NumbersIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {checkingAccount ? (
                      <CircularProgress size={20} />
                    ) : accountMatch === true ? (
                      <CheckCircleIcon color="success" />
                    ) : accountMatch === false ? (
                      <ErrorIcon color="error" />
                    ) : null}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        {/* Actions */}
        <Stack direction="row" spacing={2} mt={5} justifyContent="flex-end">
          {isDataFetched && (
            <Button
              variant="outlined"
              color="error"
              onClick={handleDelete}
              disabled={loading}
              startIcon={<DeleteIcon />}
              sx={{ px: 3, borderRadius: 2, textTransform: "none" }}
            >
              Delete Beneficiary
            </Button>
          )}

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || isDataFetched}
            startIcon={
              loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            sx={{
              px: 4,
              borderRadius: 2,
              fontWeight: "bold",
              textTransform: "none",
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
            }}
          >
            {isDataFetched ? "Saved" : "Save Details"}
          </Button>
        </Stack>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: "100%", borderRadius: 2 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default BankDetailsForm;
